import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Button,
  DatePicker,
  formatHandoverSchedule,
  pendingScheduleLeg,
  SCHEDULE_LEG_COPY,
  SearchableDropdown,
  SideBarModal,
} from '@/components';
import { Handover } from '@/types/common';
import { updateHandoverSchedule } from '@/services/api/handovers';
import {
  BookingScheduleSetting,
  getBookingScheduleSettings,
} from '@/services/api/settings';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { extractApiErrorMessage, getUserEmail } from '@/utils';

/** How far either side of today the picker offers days. */
const DAYS_BACK = 30;
const DAYS_FORWARD = 120;

/**
 * The hub's open days as concrete dates, since `DatePicker` takes a list.
 *
 * Reaches backwards as well as forwards: an override exists to fix a plan, and
 * the commonest fix is a slot that already lapsed. The server has no lead-window
 * rule on this route for the same reason — only the day-of-week gate — so the two
 * agree about what is selectable.
 *
 * Only this hub's days. Unioning across every active hub would offer a weekday
 * hub B opens for a handover the server validates against hub A, and the save
 * would come back 422 with the reason buried in a toast.
 */
const openDates = (hub: BookingScheduleSetting | null) => {
  const activeDays = new Set((hub?.activeDays || []).map(Number));
  const dates: string[] = [];
  const from = dayjs().startOf('day').subtract(DAYS_BACK, 'day');
  for (let i = 0; i < DAYS_BACK + DAYS_FORWARD; i += 1) {
    const day = from.add(i, 'day');
    // No config at all must not lock the picker: fall back to every day and let
    // the server be the one to refuse.
    if (activeDays.size > 0 && !activeDays.has(day.day())) continue;
    dates.push(day.format('YYYY-MM-DD'));
  }
  return dates;
};

interface EditScheduleModalProps {
  isOpen: boolean;
  handover: Handover | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Ops override for the date a waiting handover is due on — the pickup slot while
 * the unit is still at the hub, the return date once one is booked.
 *
 * Only ever one date: the other is either history or does not exist yet, and the
 * endpoint refuses it anyway (409 naming the rung), so offering both would be
 * offering a button that cannot work.
 */
const EditScheduleModal: React.FC<EditScheduleModalProps> = ({
  isOpen,
  handover,
  onClose,
  onSuccess,
}) => {
  const leg = pendingScheduleLeg(handover?.status);
  const isReturn = leg === 'RETURN';
  // One source for the wording this action uses in all three places it appears.
  const copy = leg ? SCHEDULE_LEG_COPY[leg] : null;

  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [hub, setHub] = useState<BookingScheduleSetting | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentDate = isReturn
    ? handover?.scheduledReturnDate
    : handover?.scheduledHandoverDate;

  useEffect(() => {
    if (!isOpen) return;
    setDate(currentDate ?? '');
    setStartTime(handover?.slotStartTime ?? '');
    setError(null);
  }, [isOpen, currentDate, handover?.slotStartTime]);

  // The hub config drives which days are selectable and which windows exist.
  //
  // The OLDEST ACTIVE row, which is exactly what the server's own getActiveHub()
  // resolves (single-hub launch — see slot-validation.service.ts). Reading the same
  // one row is what keeps the picker's offer and the server's answer in step.
  //
  // Failure is not fatal: the picker falls back to every day and the server still
  // validates, so a settings outage cannot block an urgent re-schedule.
  const loadSettings = useCallback(async () => {
    try {
      const response = await getBookingScheduleSettings({
        page: '1',
        size: '1',
        isActive: true,
        sort: 'createdAt',
        order: 'asc',
      });
      setHub(response?.data?.data?.[0] ?? null);
    } catch {
      setHub(null);
    }
  }, []);

  useEffect(() => {
    if (isOpen) loadSettings();
  }, [isOpen, loadSettings]);

  const allowedDates = useMemo(() => openDates(hub), [hub]);
  // Value/label pairs for the shared dropdown; the value is the START, which is
  // all the server takes — it resolves the end from this same config.
  const windowOptions = useMemo(
    () =>
      [...(hub?.timeWindows || [])]
        .sort((a, b) => a.startTime.localeCompare(b.startTime))
        .map((w) => ({
          value: w.startTime,
          label: `${w.startTime} - ${w.endTime}`,
        })),
    [hub]
  );

  const handleSubmit = async () => {
    if (!handover || !leg) return;
    if (!date) {
      setError('Tanggal wajib dipilih');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await updateHandoverSchedule(handover.ID, {
        ...(isReturn
          ? { scheduledReturnDate: date }
          : {
              scheduledHandoverDate: date,
              // Only when ops actually picked one — the server keeps the stored
              // window otherwise, and resolves the end time from the hub config.
              ...(startTime ? { slotStartTime: startTime } : {}),
            }),
        updatedBy: getUserEmail(),
      });
      showSuccessToast(
        'Jadwal diperbarui',
        `${copy?.noun} dijadwalkan ${formatHandoverSchedule(date)}`
      );
      onSuccess();
      onClose();
    } catch (err) {
      // Inline so it survives beside the field that caused it, and toasted once
      // for the case where the modal is scrolled away from it.
      const message = extractApiErrorMessage(err, 'Gagal memperbarui jadwal');
      setError(message);
      showErrorToast('Gagal memperbarui jadwal', message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !handover || !leg) return null;

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="center"
      width="sm"
      header={
        <div>
          <h2 className="text-[18px] font-medium text-gray-900">
            {copy?.action}
          </h2>
          <p className="text-[13px] text-gray-500 mt-0.5">
            {[handover.platNomor, handover.driverName]
              .filter(Boolean)
              .join(' • ') || '-'}
          </p>
        </div>
      }
      footer={
        <div className="flex gap-3">
          <Button
            variant="neutral"
            onClick={onClose}
            disabled={isSaving}
            className="flex-1 py-3"
          >
            Batal
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSaving || !date}
            className="flex-1 py-3"
          >
            {isSaving ? 'Menyimpan...' : 'Simpan'}
          </Button>
        </div>
      }
    >
      <div className="px-6 py-4 space-y-4">
        <div className="rounded-lg bg-gray-50 px-3 py-2.5">
          <p className="text-[13px] text-gray-500">
            Jadwal sekarang:{' '}
            <span className="text-gray-900">
              {currentDate
                ? formatHandoverSchedule(
                    currentDate,
                    isReturn
                      ? undefined
                      : {
                          startTime: handover.slotStartTime,
                          endTime: handover.slotEndTime,
                        }
                  )
                : 'belum diatur'}
            </span>
          </p>
        </div>

        <div>
          <label className="block text-[14px] text-gray-900 mb-1.5">
            {copy?.dateLabel} <span className="text-primary">*</span>
          </label>
          <DatePicker
            value={date}
            onChange={setDate}
            allowedDates={allowedDates}
            invalidDateMessage="Hub tidak buka di tanggal itu."
            placeholder="Pilih tanggal"
            className="w-full"
          />
          <p className="mt-1 text-[12px] text-gray-500">
            Hanya hari operasional hub yang bisa dipilih.
          </p>
        </div>

        {/* Pickup only. A scheduled return carries a date and nothing else — the
            slot window belongs to the collection leg. */}
        {!isReturn && windowOptions.length > 0 && (
          <div>
            <label className="block text-[14px] text-gray-900 mb-1.5">
              Slot Waktu
            </label>
            <SearchableDropdown
              options={windowOptions}
              value={startTime}
              onChange={setStartTime}
              placeholder="Biarkan seperti sekarang"
              emptyMessage="Hub belum punya slot waktu"
              allowClear
              className="w-full"
            />
          </div>
        )}

        {error && <p className="text-[13px] text-red-500">{error}</p>}
      </div>
    </SideBarModal>
  );
};

export default EditScheduleModal;
